import {
  GridCellParams,
  GridColDef,
  GridColumnGroup,
  GridColumnVisibilityModel,
  GridFilterModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import React from "react";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { z } from "zod/v4";

import {
  disableEditEventParam,
  enableEditEventParam,
  isMixHeaderRow,
  makePostDataGridProcessingCallback,
  updateRowEventParam,
} from "./utils";

import { Row } from "./types";
import { SystemStyleObject, Theme } from "@mui/system";

interface Props {
  columns: GridColDef<Row>[];
  disableEdit: boolean;
  rows: Row[];
  onChange: (rows: Row[]) => void;
  filterModel: GridFilterModel;
  columnGroupingModel: GridColumnGroup[];
  pinnedColumns: string[];
  sx: SystemStyleObject<Theme>;
  columnVisibilityModel: GridColumnVisibilityModel;
  collapsedMixIds?: ReadonlySet<number>;
}

export const Table = ({
  columns,
  disableEdit,
  rows,
  onChange,
  filterModel,
  columnGroupingModel,
  pinnedColumns,
  sx,
  columnVisibilityModel,
  collapsedMixIds,
}: Props) => {
  const [internalDisableEdit, setInternalDisableEdit] = React.useState(false);
  const apiRef = useGridApiRef();

  const handleOnChange = React.useCallback(() => {
    if (apiRef.current) {
      const rows = [...apiRef.current.getRowModels().values()] as Row[];
      onChange(rows);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- apiRef is a ref (stable identity), not a reactive dependency
  }, [onChange]);

  const postDataGridProcessingHandleOnChange = React.useMemo(
    () => makePostDataGridProcessingCallback(handleOnChange),
    [handleOnChange]
  );

  const handleIsCellEditable = React.useCallback(
    (params: GridCellParams<Row>) => {
      if (
        isMixHeaderRow(params.row) &&
        collapsedMixIds?.has(params.row.mixId)
      ) {
        return false;
      }
      return (
        !["total", "empty_basket", "empty_profile"].includes(params.row.type) ||
        !internalDisableEdit ||
        !disableEdit
      );
    },
    [disableEdit, internalDisableEdit, collapsedMixIds]
  );

  const handleStateChange = React.useCallback(
    (params: unknown) => {
      if (z.literal(updateRowEventParam).safeParse(params).success) {
        void postDataGridProcessingHandleOnChange(params);
      }
      if (z.literal(disableEditEventParam).safeParse(params).success) {
        setInternalDisableEdit(true);
      }
      if (z.literal(enableEditEventParam).safeParse(params).success) {
        setInternalDisableEdit(false);
      }
    },
    [postDataGridProcessingHandleOnChange, setInternalDisableEdit]
  );

  React.useEffect(() => {
    if (apiRef.current) {
      const unsubscribeCellEditStop = apiRef.current.subscribeEvent(
        "cellEditStop",
        (...args: Parameters<typeof postDataGridProcessingHandleOnChange>) => {
          void postDataGridProcessingHandleOnChange(...args);
        }
      );
      const unsubscribeStateChange = apiRef.current.subscribeEvent(
        "stateChange",
        handleStateChange
      );
      return () => {
        unsubscribeCellEditStop();
        unsubscribeStateChange();
      };
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- apiRef is a ref (stable identity), not a reactive dependency
  }, [postDataGridProcessingHandleOnChange, handleStateChange]);

  const cellHeight = 35;

  return (
    <HotkeyCompliantDataGrid
      apiRef={apiRef}
      columns={columns}
      rows={rows}
      rowHeight={cellHeight}
      getRowHeight={({ model }) => {
        const row = model as Row;
        return isMixHeaderRow(row) && collapsedMixIds?.has(row.mixId)
          ? cellHeight * 2
          : null;
      }}
      columnHeaderHeight={40}
      columnGroupHeaderHeight={cellHeight}
      filterModel={filterModel}
      columnGroupingModel={columnGroupingModel}
      getRowClassName={(params) => {
        switch (params.row.type) {
          case "total":
            if (params.isLastVisible) {
              return "total-row-final";
            } else {
              return "total-row";
            }
          case "basket":
          case "empty_basket":
          case "empty_profile":
          case "profile":
          case "addition":
          case "ratio":
            return isMixHeaderRow(params.row) &&
              collapsedMixIds?.has(params.row.mixId)
              ? "collapsed-row"
              : "";
        }
      }}
      rowSpanning
      isCellEditable={handleIsCellEditable}
      pinnedColumns={{ left: pinnedColumns }}
      sx={sx}
      columnVisibilityModel={columnVisibilityModel}
    />
  );
};
